import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { ExclamationCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';

import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE, MIN_TRANSFER, MIN_TRANSFER_INTERNATIONAL } from 'helpers/constants';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import useGetUser from 'v2/hooks/useGetUser';
import { useQuery } from 'v2/hooks/useQuery';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import { TransferStatus } from 'v2/interfaces/financial';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';
import { transferService } from 'v2/services/transfer';
import { transferHistoryService } from 'v2/services/transferHistory';

import { MESSAGE_LAST_TRANSFER_CANCELLED, type Props } from './constants';
import TransferModal from './TransferModal';
import ValueCard from '../ValueCard';

function AvailableCard({
  transferAccessData,
  isLoading,
  modalTransferIsOpen,
  hasValueToAnticipate,
  fetchData,
  handleToggleTransferModal,
  handleToggleAnticipateModal
}: Props) {
  useObservable(() => transferHistoryService.loadData(), []);

  const [transferHistory] = useObservable(() => transferHistoryService.getTransferHistory(), [], []);
  const [limitToTransferPF, setLimitToTransferPF] = useState(null);
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);

  const [currentBalance] = useObservable(() => financialStatementService.getCurrentBalance(), []);
  const user = useGetUser();
  const permissions = useGetPermissions();
  const { data: registryCheck, shouldBlock, dismissBlock } = useRegistryCheck();

  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const params = useQuery();
  const transferAccess = transferAccessData;
  const screenAction = params.get('a');

  const MIN_VALUE_TO_TRANSFER = user?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;
  const MESSAGE_NO_VALUE_TO_TRANSFER = `É preciso ter ao menos ${numberToBRL(MIN_VALUE_TO_TRANSFER)} disponível para solicitar transferência`;

  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
  const hasValueToTransfer = currentBalance > MIN_VALUE_TO_TRANSFER;
  const transferBlocked = transferAccess?.status === 'blocked';
  const availableToTransfer = user?.type === 'F' ? limitToTransferPF : currentBalance;

  const lastDayTransfer = transferHistory?.length ? transferHistory[0].solicitation_date : '';
  const lastTransfers = transferHistory?.filter(transfer => transfer.solicitation_date === lastDayTransfer) || [];
  const lastTransfer = lastTransfers[lastTransfers.length - 1];
  const lastTransferWasCancelled = lastTransfer?.status_id === TransferStatus.CANCELADA;

  const disableTransferButton =
    userHasBlockTransferBetaModule || !hasValueToTransfer || transferBlocked || !transferAccess || isLoading;

  const [getLimitAvailableToTransfer] = useCallbackObservable(currUser => {
    return transferService.getAvailable().pipe(
      tap(({ data }) => {
        setLimitToTransferPF(currUser.crossborder ? data.total_origin : data.total);
      }),
      catchError(error => {
        toast.error(error);
        return of(true);
      })
    );
  }, []);

  const getDisabledMessage = () => {
    if (transferBlocked) return transferAccess?.reason;
    if (userHasBlockTransferBetaModule) return FINANCIAL_BLOCK_TOOLTIP_MESSAGE;
    if (!hasValueToTransfer) return MESSAGE_NO_VALUE_TO_TRANSFER;
    return '';
  };

  const handleOpenTransferModal = useCallback(() => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Transferir'
    });

    if (shouldBlock) {
      setShowRegistryBlockModal(true);
      return;
    }

    handleToggleTransferModal();
  }, [handleToggleTransferModal, shouldBlock]);

  const handleCancelRegistryBlockModal = useCallback(() => {
    dismissBlock();
    setShowRegistryBlockModal(false);
    handleToggleTransferModal();
  }, [handleToggleTransferModal, dismissBlock]);

  const goToTransferHistory = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Histórico de Transferências'
    });
  };

  useEffect(() => {
    if (!!user) {
      fetchData();
    }
  }, [fetchData, user]);

  useEffect(() => {
    if (user?.type === 'F') {
      getLimitAvailableToTransfer(user);
    }
  }, [getLimitAvailableToTransfer, user]);

  const hasAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (screenAction !== 'transfer') return;
    if (!user) return;
    if (hasAutoOpenedRef.current) return;

    if (!disableTransferButton) {
      hasAutoOpenedRef.current = true;
      handleOpenTransferModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableTransferButton, screenAction, user]);

  return (
    <>
      <ValueCard
        id='available-card'
        title='Disponível para transferência'
        isLoading={isLoading}
        value={availableToTransfer}
        hint='Total disponível para saque'
        ctaCopy='Transferir saldo'
        ctaButtonTooltip={getDisabledMessage()}
        isCtaDisabled={disableTransferButton}
        ctaCallback={handleOpenTransferModal}
        ctaAccessControl='myeduzz:financeiro:solicitar-transferencia'
        bottomExtra={
          <>
            <Link
              className='text-grey-500!'
              to='/producer/financial-statement/transfer-history'
              onClick={goToTransferHistory}
              id='transfer-history-button'
            >
              <FileTextOutlined className='mr-1' style={{ fontSize: 14 }} />
              <span>Histórico de transferências</span>
            </Link>

            {lastTransferWasCancelled && (
              <Tooltip title={MESSAGE_LAST_TRANSFER_CANCELLED} placement='right'>
                <ExclamationCircleOutlined className='text-error mr-1' style={{ marginLeft: 10, fontSize: 14 }} />
              </Tooltip>
            )}

            {modalTransferIsOpen && (
              <TransferModal
                isOpen={modalTransferIsOpen}
                showAnticipateStep={hasValueToAnticipate}
                setClose={handleToggleTransferModal}
                handleUpdateAvailableAmount={fetchData}
                handleToggleAnticipateModal={handleToggleAnticipateModal}
              />
            )}
          </>
        }
      />

      <RegistryBlockModal
        opened={showRegistryBlockModal}
        isOwner={permissions?.owned}
        cancelable={registryCheck?.skip}
        onCancel={handleCancelRegistryBlockModal}
      />
    </>
  );
}

export default AvailableCard;
